/* =====================================================================
 * MemoWindow — 위젯 캔버스 위 플로팅 메모장
 *
 * - 글래스모피즘 디자인 (Dock 톤 유지)
 * - 기본: 실시간 마크다운 렌더링, 클릭 시 편집 모드
 * - 이미지는 별도 imageStore에 저장, 본문에는 짧은 토큰만 유지
 * - 헤더 드래그로 이동, 가장자리 드래그로 리사이즈
 * - 핀 고정 / 더보기(이미지 업로드, 삭제) 지원
 * ===================================================================== */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import styled, { css, keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Pin,
  PinOff,
  ImagePlus,
  Trash2,
  GripHorizontal,
  Loader,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Image compression                                                   */
/* ------------------------------------------------------------------ */

const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.72;
const SIZE_SKIP_THRESHOLD = 80 * 1024;

function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Not an image'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;

      if (file.size <= SIZE_SKIP_THRESHOLD && /\/(png|gif|svg)/.test(file.type)) {
        resolve(dataUrl);
        return;
      }

      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const quality = outputType === 'image/jpeg' ? JPEG_QUALITY : undefined;
        resolve(canvas.toDataURL(outputType, quality));
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

let imgSeq = 0;
function nextImgId(): string {
  return `mimg_${++imgSeq}_${Date.now().toString(36)}`;
}

/* ------------------------------------------------------------------ */
/* Image token helpers                                                 */
/* ------------------------------------------------------------------ */

const IMG_TOKEN_RE = /!\[([^\]]*)\]\(memo-img:([a-zA-Z0-9_]+)\)/g;

function resolveImageTokens(
  md: string,
  store: Record<string, string>,
): string {
  return md.replace(IMG_TOKEN_RE, (_match, alt, id) => {
    const src = store[id];
    if (!src) return `![${alt}](missing)`;
    return `![${alt}](${src})`;
  });
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface MemoData {
  id: string;
  content: string;
  imageStore: Record<string, string>;
}

export interface FloatingRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MemoWindowProps {
  memo: MemoData;
  rect: FloatingRect;
  pinned: boolean;
  opacity: number;
  zIndex: number;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, rect: Partial<FloatingRect>) => void;
  onFocus: (id: string) => void;
  onTogglePin: (id: string) => void;
  onOpacityChange: (id: string, opacity: number) => void;
  onDelete: (id: string) => void;
  onContentChange: (id: string, content: string) => void;
  onImageAdd: (id: string, imgId: string, dataUrl: string) => void;
}

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const MIN_W = 260;
const MIN_H = 200;
const HANDLE = 6;
const CORNER = 14;
const OPACITY_MIN = 35;
const OPACITY_MAX = 100;
const BASE_BG_ALPHA = 0.72;

type Dir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const DIRS: Dir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const CURSOR: Record<Dir, string> = {
  n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
  ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize',
};

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

/* ------------------------------------------------------------------ */
/* Styled                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div<{ $zIndex: number; $opacity: number }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  z-index: ${({ $zIndex }) => $zIndex};
  animation: ${fadeIn} 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  border-radius: 14px;
  background: ${({ theme, $opacity }) =>
    theme.mode === 'dark'
      ? `rgba(36, 36, 36, ${BASE_BG_ALPHA * $opacity})`
      : `rgba(255, 255, 255, ${BASE_BG_ALPHA * $opacity})`};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.10)'
        : 'rgba(0, 0, 0, 0.08)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? `0 8px 32px rgba(0,0,0,0.38),
         0 2px 8px rgba(0,0,0,0.24),
         inset 0 0.5px 0 rgba(255,255,255,0.06)`
      : `0 8px 32px rgba(0,0,0,0.10),
         0 2px 8px rgba(0,0,0,0.05),
         inset 0 0.5px 0 rgba(255,255,255,0.9)`};
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const Header = styled.div<{ $pinned: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 6px 12px;
  flex-shrink: 0;
  cursor: ${({ $pinned }) => ($pinned ? 'default' : 'grab')};
  user-select: none;

  &:active {
    cursor: ${({ $pinned }) => ($pinned ? 'default' : 'grabbing')};
  }
`;

const DragGrip = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.45;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const IconBtn = styled.button<{ $active?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  transition: all 150ms ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const DeleteIconBtn = styled(IconBtn)`
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.88)' : 'rgba(17, 24, 39, 0.92)'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0, 0, 0, 0.06)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? '#ffffff' : 'rgba(17, 24, 39, 0.92)'};
  }
`;

const OpacityControl = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
`;

const OpacityRange = styled.input`
  width: 76px;
  height: 18px;
  margin: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: ew-resize;

  &::-webkit-slider-runnable-track {
    height: 3px;
    border-radius: 999px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.18)'};
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    margin-top: -4px;
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.2)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.96)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 1px 3px rgba(0,0,0,0.45)'
        : '0 1px 3px rgba(0,0,0,0.2)'};
  }

  &::-moz-range-track {
    height: 3px;
    border-radius: 999px;
    border: none;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.18)'};
  }

  &::-moz-range-thumb {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.2)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.96)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 1px 3px rgba(0,0,0,0.45)'
        : '0 1px 3px rgba(0,0,0,0.2)'};
  }
`;

/* ---- Body ---- */

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const EditorArea = styled.textarea`
  position: absolute;
  inset: 0;
  resize: none;
  padding: 8px 14px 14px;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.85rem;
  line-height: 1.65;
  scrollbar-width: thin;
  z-index: 2;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  @media (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

const PreviewArea = styled.div`
  flex: 1;
  padding: 8px 14px 14px;
  overflow-y: auto;
  scrollbar-width: thin;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.85rem;
  line-height: 1.7;
  cursor: text;

  h1, h2, h3, h4, h5, h6 {
    margin: 0.6em 0 0.3em;
    font-weight: 600;
    line-height: 1.3;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.1rem; }
  h3 { font-size: 1rem; }

  p { margin: 0 0 0.5em; }

  ul, ol {
    margin: 0.3em 0;
    padding-left: 1.4em;
  }

  li { margin-bottom: 0.15em; }

  code {
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 0.82em;
    padding: 1px 5px;
    border-radius: 4px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }

  pre {
    padding: 10px 12px;
    border-radius: 8px;
    overflow-x: auto;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)'};

    code { padding: 0; background: transparent; }
  }

  blockquote {
    margin: 0.5em 0;
    padding: 2px 12px;
    border-left: 3px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  hr {
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    margin: 0.8em 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5em 0;
    font-size: 0.82rem;
  }

  th, td {
    padding: 5px 8px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
  }

  th {
    font-weight: 600;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }

  img {
    max-width: 100%;
    border-radius: 6px;
    margin: 0.4em 0;
  }

  a {
    color: ${({ theme }) => theme.colors.info};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  @media (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

const EmptyHint = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.82rem;
  cursor: text;
`;

/* ---- Resize handles ---- */

const ResizeHandle = styled.div<{ $dir: Dir }>`
  position: absolute;
  z-index: 10;
  ${({ $dir }) => {
    switch ($dir) {
      case 'n':  return css`top:0;left:${CORNER}px;right:${CORNER}px;height:${HANDLE}px;cursor:ns-resize;`;
      case 's':  return css`bottom:0;left:${CORNER}px;right:${CORNER}px;height:${HANDLE}px;cursor:ns-resize;`;
      case 'e':  return css`right:0;top:${CORNER}px;bottom:${CORNER}px;width:${HANDLE}px;cursor:ew-resize;`;
      case 'w':  return css`left:0;top:${CORNER}px;bottom:${CORNER}px;width:${HANDLE}px;cursor:ew-resize;`;
      case 'ne': return css`top:0;right:0;width:${CORNER}px;height:${CORNER}px;cursor:nesw-resize;`;
      case 'nw': return css`top:0;left:0;width:${CORNER}px;height:${CORNER}px;cursor:nwse-resize;`;
      case 'se': return css`bottom:0;right:0;width:${CORNER}px;height:${CORNER}px;cursor:nwse-resize;`;
      case 'sw': return css`bottom:0;left:0;width:${CORNER}px;height:${CORNER}px;cursor:nesw-resize;`;
    }
  }}
`;

const HiddenFileInput = styled.input`display:none;`;

interface MarkdownPreviewProps {
  markdown: string;
  onActivate: () => void;
  remarkPlugins: [typeof remarkGfm];
}

const passthroughUrl = (url: string) => url;

const MarkdownPreview = React.memo(function MarkdownPreview({
  markdown,
  onActivate,
  remarkPlugins,
}: MarkdownPreviewProps) {
  return (
    <PreviewArea onClick={onActivate}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        urlTransform={passthroughUrl}
      >
        {markdown}
      </ReactMarkdown>
    </PreviewArea>
  );
});

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const MemoWindow: React.FC<MemoWindowProps> = ({
  memo,
  rect,
  pinned,
  opacity,
  zIndex,
  onMove,
  onResize,
  onFocus,
  onTogglePin,
  onOpacityChange,
  onDelete,
  onContentChange,
  onImageAdd,
}) => {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ---- auto-focus editor ---- */
  useEffect(() => {
    if (editing) {
      const el = editorRef.current;
      if (el) {
        el.focus();
        el.selectionStart = el.selectionEnd = el.value.length;
      }
    }
  }, [editing]);

  /* ---- drag (move) ---- */
  const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      onMove(memo.id, d.ox + e.clientX - d.startX, d.oy + e.clientY - d.startY);
    };
    const onPointerUp = () => {
      if (dragRef.current) {
        dragRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [memo.id, onMove]);

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      if (pinned) return;
      onFocus(memo.id);
      dragRef.current = { startX: e.clientX, startY: e.clientY, ox: rect.x, oy: rect.y };
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    },
    [pinned, memo.id, rect.x, rect.y, onFocus],
  );

  /* ---- resize ---- */
  const resRef = useRef<{ dir: Dir; sx: number; sy: number; r: FloatingRect } | null>(null);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const d = resRef.current;
      if (!d) return;
      const dx = e.clientX - d.sx;
      const dy = e.clientY - d.sy;
      let { x, y, w, h } = d.r;

      if (d.dir.includes('e')) w = Math.max(MIN_W, d.r.w + dx);
      if (d.dir.includes('s')) h = Math.max(MIN_H, d.r.h + dy);
      if (d.dir.includes('w')) {
        const nw = Math.max(MIN_W, d.r.w - dx);
        x = d.r.x + (d.r.w - nw);
        w = nw;
      }
      if (d.dir.includes('n')) {
        const nh = Math.max(MIN_H, d.r.h - dy);
        y = d.r.y + (d.r.h - nh);
        h = nh;
      }
      onResize(memo.id, { x, y, w, h });
    };
    const onPointerUp = () => {
      if (resRef.current) {
        resRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [memo.id, onResize]);

  const startResize = useCallback(
    (dir: Dir, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onFocus(memo.id);
      resRef.current = { dir, sx: e.clientX, sy: e.clientY, r: { ...rect } };
      document.body.style.cursor = CURSOR[dir];
      document.body.style.userSelect = 'none';
    },
    [memo.id, rect, onFocus],
  );

  /* ---- image upload ---- */
  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      setUploading(true);

      try {
        const tokens: string[] = [];
        for (const file of Array.from(files)) {
          try {
            const dataUrl = await compressImage(file);
            const id = nextImgId();
            onImageAdd(memo.id, id, dataUrl);
            tokens.push(`![${file.name}](memo-img:${id})`);
          } catch {
            /* skip unreadable */
          }
        }
        if (tokens.length > 0) {
          const suffix = '\n' + tokens.join('\n') + '\n';
          onContentChange(memo.id, memo.content + suffix);
        }
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [memo.id, memo.content, onContentChange, onImageAdd],
  );

  /* ---- resolved markdown for rendering ---- */
  const resolvedMd = useMemo(
    () => resolveImageTokens(memo.content, memo.imageStore),
    [memo.content, memo.imageStore],
  );

  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const activateEditing = useCallback(() => setEditing(true), []);
  const opacityPercent = Math.round(Math.min(1, Math.max(0.35, opacity)) * 100);
  const handleOpacity = useCallback(
    (next: number) => {
      const normalized = Math.min(OPACITY_MAX, Math.max(OPACITY_MIN, Number(next)));
      onOpacityChange(memo.id, normalized / 100);
    },
    [memo.id, onOpacityChange],
  );

  const hasContent = memo.content.trim().length > 0;

  return (
    <Wrapper
      $zIndex={zIndex}
      $opacity={Math.min(1, Math.max(0.35, opacity))}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      onPointerDown={() => onFocus(memo.id)}
    >
      {/* Header */}
      <Header $pinned={pinned} onPointerDown={startDrag}>
        <DragGrip>
          <GripHorizontal size={14} />
        </DragGrip>
        <HeaderTitle>메모</HeaderTitle>
        <HeaderActions>
          <OpacityControl onPointerDown={(e) => e.stopPropagation()}>
            <OpacityRange
              type="range"
              min={OPACITY_MIN}
              max={OPACITY_MAX}
              step={5}
              value={opacityPercent}
              title={`투명도 ${opacityPercent}%`}
              onChange={(e) => handleOpacity(Number(e.target.value))}
            />
          </OpacityControl>

          <IconBtn
            $active={pinned}
            title={pinned ? '고정 해제' : '위치 고정'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onTogglePin(memo.id); }}
          >
            {pinned ? <PinOff size={14} /> : <Pin size={14} />}
          </IconBtn>

          <IconBtn
            title={uploading ? '이미지 압축 중...' : '이미지 업로드'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (uploading) return;
              fileRef.current?.click();
            }}
          >
            {uploading ? <Loader size={14} /> : <ImagePlus size={14} />}
          </IconBtn>

          <DeleteIconBtn
            title="삭제"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(memo.id);
            }}
          >
            <Trash2 size={14} />
          </DeleteIconBtn>
        </HeaderActions>
      </Header>

      {/* Body — click-to-edit, blur-to-render */}
      <Body>
        {editing ? (
          <EditorArea
            ref={editorRef}
            value={memo.content}
            onChange={(e) => onContentChange(memo.id, e.target.value)}
            onBlur={() => setEditing(false)}
            placeholder="마크다운으로 작성하세요..."
            spellCheck={false}
          />
        ) : hasContent ? (
          <MarkdownPreview
            markdown={resolvedMd}
            onActivate={activateEditing}
            remarkPlugins={remarkPlugins as [typeof remarkGfm]}
          />
        ) : (
          <EmptyHint onClick={activateEditing}>
            클릭하여 메모 작성...
          </EmptyHint>
        )}
      </Body>

      {/* Resize handles */}
      {DIRS.map((dir) => (
        <ResizeHandle
          key={dir}
          $dir={dir}
          onPointerDown={(e) => startResize(dir, e)}
        />
      ))}

      <HiddenFileInput
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
      />
    </Wrapper>
  );
};

export default MemoWindow;
